/**
 * Pure derivation behind the capabilities modal: which filter chips to offer,
 * and which tools survive the current use-case / entity / search selection.
 *
 * No React — this is the testable half of the modal.
 */

import { ALL_KEY, CATEGORY_ORDER, ENTITY_ORDER, USE_CASE_ORDER } from './labels.js';
import type { ChatToolCapabilityItem } from './types.js';

/**
 * Hide what this viewer could not run anyway.
 *
 * The list used to show all 361 capabilities to everyone, so a `wtc_crm` user
 * read ~164 tools they cannot call and had no way to tell which. Role badges
 * on the card were the only signal, and a badge you have to decode is not an
 * answer to "what can I ask for".
 *
 * A row with no `permittedRoles` is KEPT, not dropped: the package's view model
 * types that field as optional, and a host that does not supply it must not
 * silently render an empty browser. The server-side default-DENY (`role-filter`)
 * is the real control; this is presentation.
 */
export function visibleToRole(
  tools: readonly ChatToolCapabilityItem[],
  userRole: string | undefined
): ChatToolCapabilityItem[] {
  if (!userRole) return [...tools];
  return tools.filter((t) => !t.permittedRoles?.length || t.permittedRoles.includes(userRole));
}

/** Category chips/rows to offer: those present in the data, in canonical order. */
export function availableCategories(tools: readonly ChatToolCapabilityItem[]): string[] {
  const present = new Set<string>();
  tools.forEach((t) => present.add(t.category));
  const ordered = CATEGORY_ORDER.filter((c) => present.has(c));
  // A category the canonical order does not know still has to appear, or a new
  // one added on the Hub side becomes invisible here with nothing failing.
  const unknown = [...present].filter((c) => !CATEGORY_ORDER.includes(c)).sort();
  return [...ordered, ...unknown];
}

/** How many capabilities sit under each category, for the sidebar counts. */
export function categoryCounts(
  tools: readonly ChatToolCapabilityItem[]
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const tool of tools) {
    counts[tool.category] = (counts[tool.category] ?? 0) + 1;
  }
  return counts;
}

/** Use-case chips to offer: those present in the data, in canonical order. */
export function availableUseCaseGroups(tools: readonly ChatToolCapabilityItem[]): string[] {
  const present = new Set<string>();
  tools.forEach((t) => t.useCaseGroups?.forEach((g) => present.add(g)));
  return USE_CASE_ORDER.filter((g) => present.has(g));
}

/** Entity chips to offer: those present in the data, in canonical order. */
export function availableEntities(tools: readonly ChatToolCapabilityItem[]): string[] {
  const present = new Set<string>();
  tools.forEach((t) => t.entities?.forEach((e) => present.add(e)));
  return ENTITY_ORDER.filter((e) => present.has(e));
}

/** Everything a free-text search matches against, for one tool. */
function searchableText(tool: ChatToolCapabilityItem): string {
  return [
    tool.displayName,
    tool.name,
    tool.useCase,
    tool.category,
    tool.examplePrompt || '',
    ...(tool.examplePrompts || []),
    ...(tool.entities || []),
    ...(tool.useCaseGroups || []),
  ]
    .join(' ')
    .toLowerCase();
}

export interface CapabilityFilterState {
  useCase: string;
  entity: string;
  search: string;
  /** Sidebar selection. Optional so existing callers keep their behaviour. */
  category?: string;
}

/**
 * Every axis ANDs (not unions), and the search narrows further — picking
 * Marketing + Ticket when no tool is both is an empty list, by design.
 */
export function filterCapabilities(
  tools: readonly ChatToolCapabilityItem[],
  { useCase, entity, search, category = ALL_KEY }: CapabilityFilterState
): ChatToolCapabilityItem[] {
  const query = search.toLowerCase().trim();
  return tools.filter((t) => {
    const matchCategory = category === ALL_KEY || t.category === category;
    const matchUseCase = useCase === ALL_KEY || (t.useCaseGroups && t.useCaseGroups.includes(useCase));
    const matchEntity = entity === ALL_KEY || (t.entities && t.entities.includes(entity));
    if (query) {
      return matchCategory && matchUseCase && matchEntity && searchableText(t).includes(query);
    }
    return matchCategory && matchUseCase && matchEntity;
  });
}

/** Extra example phrasings shown under Try, deduped against the primary one. */
export const MAX_EXTRA_EXAMPLE_PROMPTS = 2;

export function extraExamplePrompts(tool: ChatToolCapabilityItem): string[] {
  return (tool.examplePrompts ?? [])
    .filter((p) => p !== tool.examplePrompt)
    .slice(0, MAX_EXTRA_EXAMPLE_PROMPTS);
}
